import { VerificationCodeDialog } from "./VerificationCodeDialog";
import { UserDto } from "@/common/api/datasources/Api";
import { useUserVerificationQuery } from "@/common/api/services/userVerification";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2,Loader2, Mail, Phone } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

interface VerificationSettingsProps {
    user: UserDto;
    onVerificationSuccess: () => void;
}

export const VerificationSettings = ({ user, onVerificationSuccess }: VerificationSettingsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [verifyingType, setVerifyingType] = React.useState<"email" | "phone" | null>(null);
    const [isEmailSending, setIsEmailSending] = React.useState(false);
    const [isPhoneSending, setIsPhoneSending] = React.useState(false);
    const { sendVerificationCodeMutation, verifyCodeMutation, verificationQuery } = useUserVerificationQuery();

    const { data: verification, isLoading: isVerificationLoading } = verificationQuery;

    const handleSendCode = async (type: "email" | "phone") => {
        try {
            const value = type === "email" ? user.email : user.phoneNumber;
            if (!value) return;

            if (type === "email") {
                setIsEmailSending(true);
            } else {
                setIsPhoneSending(true);
            }

            await sendVerificationCodeMutation.mutateAsync({
                type,
                value,
            });

            setVerifyingType(type);

            toast({
                title: t("settings.verification.code.sent.title"),
                description: t(
                    `settings.verification.code.sent.description.${type}`
                ),
            });
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("settings.verification.code.error.title"),
                description: t("settings.verification.code.error.description"),
            });
        } finally {
            if (type === "email") {
                setIsEmailSending(false);
            } else {
                setIsPhoneSending(false);
            }
        }
    };

    const handleVerifyCode = async (code: string) => {
        if (!verifyingType) return;

        try {
            const value = verifyingType === "email" ? user.email : user.phoneNumber;
            if (!value) return;

            await verifyCodeMutation.mutateAsync({
                type: verifyingType,
                value,
                code,
            });

            toast({
                title: t("settings.verification.success.title"),
                description: t(
                    `settings.verification.success.description.${verifyingType}`
                ),
            });

            setVerifyingType(null);
            onVerificationSuccess();
            verificationQuery.refetch();
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("settings.verification.error.title"),
                description: t("settings.verification.error.description"),
            });
        }
    };

    if (isVerificationLoading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>{t("settings.verification.title")}</CardTitle>
                    <CardDescription>
                        {t("settings.verification.description")}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <Mail className="h-5 w-5 text-muted-foreground" />
                                <div>
                                    <Label>{t("settings.verification.email")}</Label>
                                    <p className="text-sm text-muted-foreground">{user.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                {verification?.isEmailVerified ? (
                                    <Badge variant="default" className="bg-green-100 text-green-800">
                                        <CheckCircle2 className="mr-1 h-3 w-3" />
                                        {t("settings.verification.verified")}
                                    </Badge>
                                ) : (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleSendCode("email")}
                                        disabled={isEmailSending}
                                    >
                                        {isEmailSending ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : null}
                                        {t("settings.verification.send")}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {user.phoneNumber && (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <Phone className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <Label>{t("settings.verification.phone")}</Label>
                                        <p className="text-sm text-muted-foreground">{user.phoneNumber}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    {verification?.isPhoneVerified ? (
                                        <Badge variant="default" className="bg-green-100 text-green-800">
                                            <CheckCircle2 className="mr-1 h-3 w-3" />
                                            {t("settings.verification.verified")}
                                        </Badge>
                                    ) : (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleSendCode("phone")}
                                            disabled={isPhoneSending}
                                        >
                                            {isPhoneSending ? (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            ) : null}
                                            {t("settings.verification.send")}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <VerificationCodeDialog
                isOpen={verifyingType !== null}
                onClose={() => setVerifyingType(null)}
                onSubmit={handleVerifyCode}
                isLoading={verifyCodeMutation.isPending}
                type={verifyingType || "email"}
            />
        </>
    );
}; 